<template>
    <section class="layout">
        <div class="header-line">
            <div class="profile-label">
                <h3>Tags</h3>

                <ds-text-button
                    dense
                    data-qa="from-billing-shipping"
                    trailing-icon="external-link"
                    @click="manageTags"
                >
                    Manage tags
                </ds-text-button>
            </div>

            <div class="tag-multiselect">
                <div class="select-box">
                    <ds-multiselect
                        ref="multiselect"
                        v-model="selectedTags"
                        multiple
                        searchable
                        :placeholder="multiSelectPlaceholder"
                        data-input-label="search-tags"
                        data-qa="add-tags"
                        :options="availableTagOptions"
                        class="multiselect-dropdown"
                    />
                </div>

                <div class="add-button">
                    <ds-filled-button
                        data-button-label="apply-tags"
                        :disabled="!isTagsSelected"
                        data-qa="apply-button"
                        @click="applyContactTags"
                    >
                        Apply
                    </ds-filled-button>
                </div>
            </div>
        </div>

        <template v-if="isContactHasTags" lang="html">
            <div class="table-scroller">
                <ds-table-grid
                    ref="table"
                    :data="tags"
                    align="center"
                    data-qa="tags-table"
                >
                    <ds-table-grid-column
                        label="Date Applied"
                        width="130"
                        prop="dateApplied"
                    >
                        <template slot-scope="tag">
                            <span> {{ dateFormatDate(tag.dateApplied, 'MMM D, YYYY') }} </span>
                        </template>
                    </ds-table-grid-column>

                    <ds-table-grid-column
                        label="Tag Applied"
                        prop="name"
                        width="450"
                    >
                        <template slot-scope="tag">
                            <ds-tooltip
                                wrap
                                class="tag-name-field"
                            >
                                <template #reference>
                                    <span class="tag-name">
                                        {{ tag.name }}
                                    </span>
                                </template>

                                <template #default>
                                    <div>{{ tag.name }}</div>
                                </template>
                            </ds-tooltip>
                        </template>
                    </ds-table-grid-column>

                    <ds-table-grid-column
                        label="Category"
                        width="200"
                        prop="category"
                    >
                        <template slot-scope="tag">
                            <span> {{ tag.category || 'Uncategorized' }} </span>
                        </template>
                    </ds-table-grid-column>

                    <ds-table-grid-column
                        label="Remove from Contact"
                        width="150"
                        align="center"
                        prop="remove"
                        data-qa="remove-button"
                    >
                        <template slot-scope="tag" data-qa="remove-button">
                            <div class="remove-tag" @click="removeTags(tag)">
                                <img
                                    alt="Delete Tag"
                                    src="/resources/funnel/images/svg/minus.svg"
                                    onmouseover="this.src='/resources/funnel/images/svg/click.svg'"
                                    onmouseout="this.src='/resources/funnel/images/svg/minus.svg'"
                                    class="add-img"
                                />
                            </div>
                        </template>
                    </ds-table-grid-column>
                </ds-table-grid>
            </div>
        </template>

        <div v-if="!isContactHasTags" class="no-tags-section">
            <div class="line-straight-down" data-qa="no-tags">
                <p class="no-tags-label">
                    No tags applied to {{ contact.firstName }}
                </p>
            </div>
        </div>
    </section>
</template>

<script>
import axios from 'axios';
import moment from 'moment';

export default {
    props: {
        contact: Object,
        tabName: String,
    },

    data() {
        return {
            url: '/app/contactGroup',
            tags: [],
            allTags: [],
            selectedTags: [],
            limit: 0,
            maxLimit: 1000,
            offset: 0,
            loaded: false,
            contactId: this.contact.id,
            isContactTagsLimitIsEnabled: false,
        };
    },

    created() {
        if (!this.loaded) {
            this.initTab();
            this.loaded = true;
        }
    },

    computed: {
        availableTagOptions() {
            let availableTags = this.allTags.map(({ category, name, id }) => {
                return {
                    category: category.name == null ? 'Uncategorized' : category.name,
                    value: id,
                    label: name,
                };
            }).sort((firstTag, secondTag) => {
                return firstTag.category < secondTag.category ? -1 : firstTag.category > secondTag.category ? 1 : 0;
            });

            availableTags = availableTags.reduce((previous, current) => {
                const key = current.category;

                if (previous[key]) {
                    previous[key].options.push({ value: current.value, label: current.label });
                } else {
                    previous[key] = {};
                    previous[key].options = [];
                    previous[key].label = key;
                    previous[key].options.push({ value: current.value, label: current.label });
                }

                return previous;
            }, []);
            availableTags = Object.keys(availableTags).map((element) => {
                return availableTags[element];
            });

            return availableTags;
        },

        isContactHasTags() {
            return this.tags.length > 0;
        },

        multiSelectPlaceholder() {
            return `Apply tag to ${this.contact.firstName}`;
        },

        isTagsSelected() {
            return this.selectedTags.length > 0;
        },
    },

    methods: {
        async loadContactTags() {
            try {
                const response = await axios.get(`${this.url}/${this.contactId}/tags`);

                this.tags = response.data.sort((a, b) => moment(b.dateApplied) - moment(a.dateApplied));
            } catch (error) {
                this.$error({ message: 'Unable to get all Tags for contact' });
            }
        },

        async removeTags(tag) {
            try {
                await axios.delete(`${this.url}/${this.contactId}/tags/${tag.id}`);
                this.loadContactTags();
            } catch (e) {
                this.$error({ message: 'Unable to remove  Tags' });
            }
        },

        async applyContactTags() {
            const selectedIds = this.selectedTags.map(({ value }) => value);

            try {
                await axios.post(`${this.url}/${this.contactId}/tags`, selectedIds);
                this.selectedTags = [];
                this.loadContactTags();
            } catch (e) {
                this.selectedTags = [];
                this.$error({ message: 'Unable to apply tag' });
            }
        },

        dateFormatDate(date, format = 'll') {
            const formattedDate = moment(date).isValid() ? date : new Date(date);

            return moment(formattedDate).format(format);
        },

        async fetchAllTags() {
            try {
                await this.isContactTagsLimitFlagIsEnabled();
                let tagUrl = '';

                if (this.isContactTagsLimitIsEnabled) {
                    tagUrl = `${this.url}/allTags?limit=${this.limit}&offset=${this.offset}`;
                } else {
                    tagUrl = `${this.url}/allTags?limit=${this.maxLimit}&offset=${this.offset}`;
                }
                const response = await axios.get(tagUrl);

                this.allTags = response.data;
            } catch (e) {
                this.$error({ message: 'Unable to get all tags' });
            }
        },

        async isContactTagsLimitFlagIsEnabled() {
            try {
                const response = await axios.get(`${this.url}/isContactTagsLimitIsEnabled`);

                this.isContactTagsLimitIsEnabled = response.data;
            } catch (e) {
                this.$error({ message: 'Unable to get all tags' });
            }
        },

        initTab() {
            this.loadContactTags();
            this.fetchAllTags();
        },

        manageTags() {
            window.open('/Reports/searchTemplate.jsp?reportClass=com.infusion.util.report.reports.setup.ContactGroupSearch&view=resultsPage&RunSearch=true&returnTo=/app/miscSetting/showSection?systemId=nav.crm&settingModuleName=PersonCompany&settingTabName=contactGroup.plural');
        },
    },
};
</script>

<style lang="scss" scoped>
.line-straight-down {
    margin: $spacing-400 0;
}

.header-line {
    padding: $spacing-300 0;
    display: flex;
    align-items: center;
    justify-content: space-between;
}

.profile-label {
    display: flex;
    align-items: center;

    h2 {
        margin-right: $spacing-100;
    }
}

.tag-multiselect {
    --input-margin-bottom: 0;
    display: flex;
}

.select-box {
    width: px-to-rem(300px);
    height: auto;
}

.remove-tag {
    text-align: center;
    margin-right: auto;
    margin-right: auto;
}

.add-button {
    padding-left: $spacing-100;
}

.table-scroller {
    white-space: nowrap;
}

.no-tags-section {
    width: 100%;
    text-align: center;
    border-top: 1px solid $color-gray-lighter;
    border-top:1px solid $color-gray-lighter;
}

.no-tags-label {
    color: $color-text;
    font-size: 1.2rem;
    font-weight: 300;
    padding-top: $spacing-800;
    padding-bottom: $spacing-600;
    line-height: normal;
}

.tag-name {
    text-overflow: ellipsis;
    white-space: nowrap;
    overflow: hidden;
    max-width: px-to-rem(350px);
    display: block;
}

.tag-name-field {
    &.tooltip-container {
        display: block;
        width: px-to-rem(360px);
    }
}
@media only screen and ($small) {
    .header-line {
        align-items: flex-start;
        flex-direction: column;
    }

    .tag-multiselect {
        margin-top: $spacing-200;
    }
}

.multiselect-dropdown {
    --dropdown-height: #{px-to-rem(330px)};
}
</style>
